/**
 * PersonalLibraryPage — /library
 * The user's central collection of personalised images they've gathered
 * across the app (Aurum snapshots, vision moments, generated visuals).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { useLibrary } from '../hooks/usePersonalLibrary';

const SOURCE_LABEL = {
  aurum_snapshot: 'Aurum',
  make_it_mine: 'Personalised',
  vision_board: 'Vision',
  gift_moment: 'Moment',
  upload: 'Uploaded',
  unknown: '',
};

const PersonalLibraryPage = () => {
  const navigate = useNavigate();
  const { items, loading, remove } = useLibrary();
  const [zoom, setZoom] = useState(null);

  return (
    <div
      className="min-h-screen relative pb-24"
      style={{ background: 'linear-gradient(180deg, #050402 0%, #070605 60%, #050402 100%)' }}
      data-testid="personal-library-page"
    >
      <header className="px-6 pt-6 pb-4 flex items-center max-w-md mx-auto">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="library-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-white/55" />
        </button>
        <h1 className="ml-2 text-[12px] tracking-[0.32em] uppercase text-white/55">My Library</h1>
      </header>

      <div className="px-6 max-w-md mx-auto">
        <h2 className="text-2xl text-white font-extralight leading-tight">
          Your personalised<br /> reality.
        </h2>
        <p className="mt-3 text-sm text-white/55">
          Every image you've collected, generated, or saved across the app.
          Reuse them anywhere — vision boards, moments, alignment.
        </p>

        {loading && (
          <p className="mt-12 text-center text-sm text-white/35">Loading…</p>
        )}

        {!loading && items.length === 0 && (
          <div
            className="mt-10 rounded-2xl p-10 text-center"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px dashed rgba(255,255,255,0.10)',
            }}
            data-testid="library-empty"
          >
            <ImageIcon size={36} className="mx-auto text-white/30 mb-3" strokeWidth={1.4} />
            <p className="text-white/65 text-sm font-light mb-1">Your library is empty.</p>
            <p className="text-white/40 text-xs leading-relaxed max-w-xs mx-auto">
              Save an Aurum snapshot, generate a personalised image, or pin a vision moment
              and it'll appear here.
            </p>
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-7 grid grid-cols-2 gap-3" data-testid="library-grid">
            {items.map((it) => (
              <motion.button
                key={it.id}
                layout
                whileTap={{ scale: 0.97 }}
                onClick={() => setZoom(it)}
                data-testid={`library-item-${it.id}`}
                className="relative aspect-[4/5] rounded-xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <img src={it.image_url} alt={it.label || ''} className="w-full h-full object-cover" loading="lazy" />
                {SOURCE_LABEL[it.source] && (
                  <span
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-medium"
                    style={{ background: 'rgba(0,0,0,0.55)', color: 'rgba(255,255,255,0.85)' }}
                  >
                    {SOURCE_LABEL[it.source]}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* Zoom modal */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 flex flex-col"
            onClick={() => setZoom(null)}
            data-testid="library-zoom"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(null); }}
              className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20"
              data-testid="library-zoom-close"
            >
              <X size={18} className="text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Remove this from your library?')) {
                  remove(zoom.id);
                  setZoom(null);
                }
              }}
              className="absolute top-6 left-6 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-red-500/40"
              data-testid="library-zoom-delete"
            >
              <Trash2 size={16} className="text-white/85" />
            </button>
            <div className="flex-1 flex items-center justify-center p-6">
              <img src={zoom.image_url} alt={zoom.label || ''} className="max-w-full max-h-full object-contain" />
            </div>
            {zoom.label && (
              <p className="text-center text-white/65 text-sm pb-8 px-6">{zoom.label}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalLibraryPage;
